import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Trash2 } from "lucide-react";
import GoogleDrivePicker, {
  IGooglePickerItem,
  useNativeGooglePicker,
} from "../Integrations/GoogleDrivePicker";
import { CustomOneDrivePicker } from "./CustomOneDrivePicker";
import type { IOneDrivePickerItem } from "./OneDrivePickerField";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { Button } from "../shadcn/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";
import { cn } from "@/lib/utils";
import { NoxusItem } from "../UI/NoxusItem";

interface IGoogleDrivePickerField {
  name: string;
  label: string;
  canMultiselect?: boolean;
  canSelectDirectories?: boolean;
  allowedFiles?: string;
  control: Control<any>;
  rules?: RegisterOptions;
  defaultValue?: IGooglePickerItem[];
  setIncludeFolders?: boolean;
  isDisabled?: boolean;
  credentialId?: string;
}

/** Maps a gdrive file_type MIME string to our picker's mimeTypeFilter.
 *  Only activates when the value targets a *single* Google Apps type
 *  (e.g. SPREADSHEETS = "application/vnd.google-apps.spreadsheet").
 *  Multi-type values like ANY or DOCUMENTS contain commas and should
 *  show all files — not restrict to a single type.  */
function getMimeTypeFilter(
  fileType: string | null | undefined
): "spreadsheet" | "document" | "presentation" | undefined {
  if (!fileType || fileType.includes(",")) return undefined;
  if (fileType.includes("vnd.google-apps.spreadsheet")) return "spreadsheet";
  if (fileType.includes("vnd.google-apps.document")) return "document";
  if (fileType.includes("vnd.google-apps.presentation")) return "presentation";
  return undefined;
}

/** Resolve a click-to-open URL for a Google Drive picked item.
 *  Prefers the explicit `url` returned by the backend listing; falls back
 *  to the canonical drive.google.com/open?id=… link which works for any
 *  file the user has access to. Returns null only if we have neither. */
function getGoogleDriveOpenUrl(item: IOneDrivePickerItem): string | null {
  if (item.url) return item.url;
  if (item.id) return `https://drive.google.com/open?id=${item.id}`;
  return null;
}

function openExternalUrl(url: string) {
  // noopener prevents the opened page from manipulating window.opener.
  const win = window.open(url, "_blank", "noopener,noreferrer");
  win?.focus();
}

export const GoogleDrivePickerField = ({
  label,
  name,
  canMultiselect = false,
  canSelectDirectories = false,
  allowedFiles,
  control,
  rules,
  defaultValue,
  setIncludeFolders,
  isDisabled,
  credentialId,
}: IGoogleDrivePickerField) => {
  const [selectedItems, setSelectedItems] = useState<IGooglePickerItem[]>(
    defaultValue || []
  );
  const nativePicker = useNativeGooglePicker();

  // Track the previous credentialId so we can detect actual changes
  const prevCredentialIdRef = useRef(credentialId);
  const clearFieldRef = useRef<((v: IGooglePickerItem[]) => void) | null>(null);

  useEffect(() => {
    if (
      prevCredentialIdRef.current != null &&
      credentialId != null &&
      prevCredentialIdRef.current !== credentialId
    ) {
      setSelectedItems([]);
      clearFieldRef.current?.([]);
    }
    prevCredentialIdRef.current = credentialId;
  }, [credentialId]);

  if (!nativePicker) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          clearFieldRef.current = field.onChange;
          return (
            <>
              {label && <NoxusFormLabel label={label} name={name} />}
              <CustomOneDrivePicker
                serviceName="GoogleDrive"
                onSelect={(items: IOneDrivePickerItem[]) => {
                  setSelectedItems(items);
                  field.onChange(items);
                }}
                selectedItems={selectedItems as IOneDrivePickerItem[]}
                canMultiselect={canMultiselect}
                canSelectDirectories={canSelectDirectories}
                mimeTypeFilter={getMimeTypeFilter(allowedFiles)}
                isDisabled={isDisabled}
                credentialId={credentialId}
              />
              {selectedItems.length > 0 && (
                <div className="flex flex-col w-full gap-2 mt-2 max-h-60 overflow-y-auto">
                  {selectedItems.map((item) => {
                    const openUrl = getGoogleDriveOpenUrl(
                      item as IOneDrivePickerItem
                    );
                    // Row is interactive only when we have a URL AND the
                    // field isn't disabled. Without the disabled gate,
                    // clicks would still fire while every other affordance
                    // (trash button, etc.) is greyed out.
                    const isInteractive = !!openUrl && !isDisabled;
                    const row = (
                      <div
                        // role/tabIndex/onKeyDown make this keyboard-reachable
                        // per DESIGN.md §10 (interactive non-buttons need a
                        // button role + key handler).
                        role={isInteractive ? "button" : undefined}
                        tabIndex={isInteractive ? 0 : undefined}
                        onClick={
                          isInteractive
                            ? () => openExternalUrl(openUrl)
                            : undefined
                        }
                        onKeyDown={
                          isInteractive
                            ? (e: KeyboardEvent<HTMLDivElement>) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openExternalUrl(openUrl);
                                }
                              }
                            : undefined
                        }
                        className={cn(
                          "w-full",
                          isInteractive && "cursor-pointer"
                        )}
                      >
                        <NoxusItem
                          icon={item.image || undefined}
                          iconAlt={item.name}
                          title={item.name}
                          iconClassName="bg-white"
                          className="bg-background2"
                          actions={
                            <Button
                              aria-label="Remove"
                              variant="ghost"
                              size="iconsm"
                              disabled={isDisabled}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const updated = selectedItems.filter(
                                  (i) => i.id !== item.id
                                );
                                setSelectedItems(updated);
                                field.onChange(updated);
                              }}
                            >
                              <Trash2 strokeWidth={1.33} />
                            </Button>
                          }
                        />
                      </div>
                    );
                    return isInteractive ? (
                      <Tooltip key={item.id}>
                        <TooltipTrigger asChild>{row}</TooltipTrigger>
                        <TooltipContent>Open in Google Drive</TooltipContent>
                      </Tooltip>
                    ) : (
                      <div key={item.id}>{row}</div>
                    );
                  })}
                </div>
              )}
            </>
          );
        }}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => (
        <GoogleDrivePicker
          label={label}
          selectedItems={field.value || []}
          setSelectedItems={(items) => field.onChange(items)}
          canMultiselect={canMultiselect}
          canSelectDirectories={canSelectDirectories}
          allowedFiles={allowedFiles}
          setIncludeFolders={setIncludeFolders}
          isDisabled={isDisabled}
        />
      )}
    />
  );
};
